/**
 * The panel *is* the editor: one full-bleed CodeMirror instance, no toolbar, no
 * gutter, no preview pane. Markdown stays visible as source and `theme.ts`
 * makes it readable.
 *
 * The single rule this component is built around: the view is created once and
 * never again. Settings arrive as props, but rebuilding the view on a font-size
 * change would throw away the caret, the scroll position and the undo history,
 * so everything that can change is routed through a `Compartment` instead.
 */

import {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from 'react'
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands'
import { markdown, markdownLanguage } from '@codemirror/lang-markdown'
import {
  Annotation,
  Compartment,
  EditorSelection,
  EditorState,
  Prec,
  Transaction,
} from '@codemirror/state'
import {
  EditorView,
  drawSelection,
  dropCursor,
  keymap,
  rectangularSelection,
} from '@codemirror/view'
import { markdownKeymap } from './markdown-keymap'
import { editorHighlight, editorTheme } from './theme'

export interface MarkdownEditorHandle {
  focus(): void
  flush(): void
}

export interface MarkdownEditorProps {
  value: string
  onChange: (value: string) => void
  fontSize: number
  lineWrap: boolean
  spellCheck: boolean
  theme: 'light' | 'dark'
  autoFocus?: boolean
  onEscape?: () => void
  /**
   * Show the text without letting it be edited.
   *
   * For the Conflict pane's preview, which puts a body the user is deciding about
   * into the real editor so it reads exactly as it would once kept (#7). Typing into
   * a body that is about to be discarded is not a state worth having, so the preview
   * is inert — and `EditorView.editable` is what keeps the caret out of it, where
   * `EditorState.readOnly` alone would still take focus.
   */
  readOnly?: boolean
  ref?: Ref<MarkdownEditorHandle>
}

/** Tags the transactions this component dispatches to mirror `props.value`. */
const fromProps = Annotation.define<boolean>()

export function MarkdownEditor({
  value,
  onChange,
  fontSize,
  lineWrap,
  spellCheck,
  theme,
  autoFocus,
  onEscape,
  readOnly = false,
  ref,
}: MarkdownEditorProps): ReactNode {
  const hostRef = useRef<HTMLDivElement>(null)
  const viewRef = useRef<EditorView | null>(null)

  // CodeMirror callbacks outlive every render, so the props they need are read
  // through refs rather than captured.
  const onChangeRef = useRef(onChange)
  const onEscapeRef = useRef(onEscape)
  useEffect(() => {
    onChangeRef.current = onChange
    onEscapeRef.current = onEscape
  })

  /**
   * The last text CodeMirror and the parent agreed on. It is what tells an
   * echo of our own edit apart from a genuinely new document, and it doubles as
   * the initial doc so a remount (React StrictMode does one) restores the text
   * that was actually on screen.
   */
  const syncedRef = useRef(value)

  const [compartments] = useState(() => ({
    /** Theme and font size both feed `editorTheme`, so they share a slot. */
    appearance: new Compartment(),
    lineWrap: new Compartment(),
    /** `spellcheck`, which has to follow the setting without a rebuild. */
    content: new Compartment(),
    /** Reconfigured to throw the undo stack away when the document is replaced. */
    history: new Compartment(),
    /** The preview is inert; the panel's own editor never is. */
    editable: new Compartment(),
  }))

  const appearance = useMemo(() => editorTheme({ theme, fontSize }), [theme, fontSize])
  const wrapping = useMemo(() => (lineWrap ? EditorView.lineWrapping : []), [lineWrap])
  const editable = useMemo(
    () => (readOnly ? [EditorState.readOnly.of(true), EditorView.editable.of(false)] : []),
    [readOnly],
  )
  const contentAttributes = useMemo(
    () =>
      EditorView.contentAttributes.of({
        spellcheck: String(spellCheck),
        'aria-label': 'Note editor',
      }),
    [spellCheck],
  )

  useEffect(() => {
    const host = hostRef.current
    if (!host) return

    const view = new EditorView({
      parent: host,
      state: EditorState.create({
        doc: syncedRef.current,
        extensions: [
          compartments.history.of(history()),
          drawSelection(),
          dropCursor(),
          rectangularSelection(),
          // Deliberately no `highlightActiveLine`: a full-width band on the one
          // line of an empty note makes a blank scratchpad look like a form
          // field. The caret already says where you are.

          compartments.appearance.of(appearance),
          compartments.lineWrap.of(wrapping),
          compartments.content.of(contentAttributes),
          compartments.editable.of(editable),
          editorHighlight(),

          // `addKeymap: false` hands Enter to our own list handling; the HTML
          // tag completion would be the only tooltip in an app that has none.
          markdown({
            base: markdownLanguage,
            codeLanguages: [],
            addKeymap: false,
            completeHTMLTags: false,
          }),

          Prec.highest(
            keymap.of([
              {
                key: 'Escape',
                run: (target) => {
                  const handler = onEscapeRef.current
                  if (!handler) return false
                  // Anything CodeMirror has opened owns Escape first.
                  if (target.dom.querySelector('.cm-panel, .cm-tooltip')) return false
                  // Escape-then-Tab is how a keyboard user gets out of an
                  // editor that binds Tab; swallowing the key would break it.
                  target.setTabFocusMode(2000)
                  handler()
                  return true
                },
              },
            ]),
          ),
          Prec.high(keymap.of(markdownKeymap)),
          keymap.of([...defaultKeymap, ...historyKeymap]),

          EditorView.updateListener.of((update) => {
            if (!update.docChanged) return
            if (update.transactions.some((tr) => tr.annotation(fromProps))) return
            const text = update.state.doc.toString()
            syncedRef.current = text
            onChangeRef.current(text)
          }),
        ],
      }),
    })

    viewRef.current = view
    if (autoFocus) view.focus()

    return () => {
      viewRef.current = null
      view.destroy()
    }
    // Mount only, deliberately. The compartments below keep the live view in
    // step with props; nothing here may depend on one.
  }, [])

  useEffect(() => {
    viewRef.current?.dispatch({ effects: compartments.appearance.reconfigure(appearance) })
  }, [compartments, appearance])

  useEffect(() => {
    viewRef.current?.dispatch({ effects: compartments.lineWrap.reconfigure(wrapping) })
  }, [compartments, wrapping])

  useEffect(() => {
    viewRef.current?.dispatch({ effects: compartments.content.reconfigure(contentAttributes) })
  }, [compartments, contentAttributes])

  useEffect(() => {
    viewRef.current?.dispatch({ effects: compartments.editable.reconfigure(editable) })
  }, [compartments, editable])

  useEffect(() => {
    const view = viewRef.current
    if (!view) return

    const current = view.state.doc.toString()
    if (current === value) {
      syncedRef.current = value
      return
    }
    // The parent echoes our own edits back one render later. Re-applying one
    // would wipe out anything typed in between, so only text the parent changed
    // on its own — a note switch, a reload from disk — gets written in.
    if (value === syncedRef.current) return

    syncedRef.current = value
    const { anchor, head } = view.state.selection.main
    view.dispatch({
      changes: { from: 0, to: current.length, insert: value },
      selection: EditorSelection.range(
        Math.min(anchor, value.length),
        Math.min(head, value.length),
      ),
      // `addToHistory: false` is the load-bearing half. Without it this
      // replace-all is an undoable event whose "before" state is another note's
      // text (or the empty document the editor mounts with), so one Ctrl+Z
      // would pull that text in and autosave it over the note on screen.
      annotations: [fromProps.of(true), Transaction.addToHistory.of(false)],
    })
    // And the stack that belonged to the outgoing document has to go with it:
    // replaying those edits against this text would corrupt it just as badly.
    // Dropping the extension and putting it back is what resets the field.
    view.dispatch({ effects: compartments.history.reconfigure([]) })
    view.dispatch({ effects: compartments.history.reconfigure(history()) })
  }, [compartments, value])

  useImperativeHandle(
    ref,
    () => ({
      focus: () => viewRef.current?.focus(),
      /**
       * A no-op. `updateListener` runs inside `dispatch`, so `onChange` has
       * already fired synchronously by the time any caller could reach this;
       * nothing is buffered. It stays on the handle so a caller about to hide
       * the panel or switch notes has one obvious thing to call, and so this
       * component can start buffering later without touching its callers.
       */
      flush: () => {},
    }),
    [],
  )

  // The host has to be given a bounded height by its parent; CodeMirror scrolls
  // inside it rather than growing the panel.
  return <div ref={hostRef} className="h-full min-h-0 overflow-hidden" />
}
